import { useEffect, useRef, useState } from "react";
import { cellGeometry, photoSrc } from "@/lib/designer";

// Source px (before 2× upscale), calibrated on the 180 cm console (782 px). Plate bottom hangs 15 cm above it.
const SCENE = { src: "/habitacion/salvia-pared.webp", w: 650, h: 480, ppc: 782 / 180, ax: 324, by: 375 };
const DETAIL_FILL = 0.56;

export const EXAMPLES = {
  vertical: { src: "/productos/noah-rolo.webp", pos: "45% 40%", alt: "Imagen de ejemplo: niño abrazado a su perro" },
  horizontal: { src: "/productos/hombre-retrievers.webp", pos: "55% 40%", alt: "Imagen de ejemplo: hombre con tres golden retriever" },
};

export const PlateCells = ({ w, h, rects, cells, photos, orientation }) => {
  const empty = !cells.some((c) => photos[c.photo_id]);
  if (empty) {
    const ex = EXAMPLES[orientation];
    return <img src={ex.src} alt={ex.alt} className="absolute inset-0 h-full w-full object-cover" style={{ objectPosition: ex.pos }} data-testid="plate-example-image" />;
  }
  return rects.map((r, i) => {
    const cw = r[2] * w, ch = r[3] * h;
    const photo = photos[cells[i]?.photo_id];
    const geo = photo && cw > 0 ? cellGeometry(photo, cells[i], cw, ch) : null;
    return (
      <div key={i} className="absolute overflow-hidden bg-[#EAE7E2]" style={{ left: r[0] * w, top: r[1] * h, width: cw, height: ch }}>
        {geo && <div className="absolute" style={geo.wrap}><img src={photoSrc(photo.id)} alt="" draggable={false} className="absolute max-w-none" style={geo.img} /></div>}
      </div>
    );
  });
};

const useWidth = () => {
  const ref = useRef(null);
  const [w, setW] = useState(0);
  useEffect(() => {
    const ro = new ResizeObserver(([e]) => setW(e.contentRect.width));
    ro.observe(ref.current);
    return () => ro.disconnect();
  }, []);
  return [ref, w];
};

const Arrow = ({ vertical, length, label, testId }) => (
  <div data-testid={testId} className={`pointer-events-none absolute flex items-center justify-center ${vertical ? "flex-col" : ""}`}
    style={vertical ? { height: length, width: 0 } : { width: length, height: 0 }}>
    <span className={`absolute bg-[#080808]/70 ${vertical ? "inset-y-0 left-0 w-px" : "inset-x-0 top-0 h-px"}`} />
    <span className={`absolute border-[#080808]/70 ${vertical ? "top-0 -left-[3px] h-0 w-0 border-x-[3.5px] border-b-[6px] border-x-transparent" : "left-0 -top-[3px] h-0 w-0 border-y-[3.5px] border-r-[6px] border-y-transparent"}`} />
    <span className={`absolute border-[#080808]/70 ${vertical ? "bottom-0 -left-[3px] h-0 w-0 border-x-[3.5px] border-t-[6px] border-x-transparent" : "right-0 -top-[3px] h-0 w-0 border-y-[3.5px] border-l-[6px] border-y-transparent"}`} />
    <span className="relative whitespace-nowrap rounded-full bg-[#FAF8F4] px-2.5 py-0.5 text-[0.7rem] font-semibold text-[#080808] shadow-sm">{label}</span>
  </div>
);

const clamp = (v, a, b) => Math.min(b, Math.max(a, v));
const Pill = ({ children, className = "", testId }) => (
  <span data-testid={testId} className={`rounded-full bg-[#FAF8F4]/90 px-2.5 py-0.5 text-[0.6rem] font-semibold uppercase tracking-[0.12em] text-[#080808] sm:px-3 sm:py-1 sm:text-[0.64rem] ${className}`}>{children}</span>
);

// mode "detail": zooms the WHOLE scene (wall, furniture and plate together) so the plate is large; "scale": fixed frame, common scale.
export const RoomPreview = ({ wcm, hcm, rects, cells, photos, orientation, label, mode = "scale", onPlateClick, testId = "room-preview" }) => {
  const [ref, W] = useWidth();
  const H = (W * SCENE.h) / SCENE.w;
  const k0 = (W / SCENE.w) * SCENE.ppc;
  const cx0 = (SCENE.ax / SCENE.w) * W, bottom0 = (SCENE.by / SCENE.h) * H;
  const cy0 = bottom0 - (hcm * k0) / 2;
  const z = mode === "detail" ? Math.max(1, Math.min(3, (H * DETAIL_FILL) / (hcm * k0), (W * 0.7) / (wcm * k0))) : 1;
  const tx = clamp(W / 2 - z * cx0, W - z * W, 0), ty = clamp(H * 0.55 - z * cy0, H - z * H, 0);
  const k = k0 * z;
  const pw = wcm * k, ph = hcm * k;
  const left = tx + z * (cx0 - (wcm * k0) / 2), top = ty + z * (bottom0 - hcm * k0);
  const empty = !cells.some((c) => photos[c.photo_id]);
  const r = Math.max(3, pw * 0.018);
  const ease = ".45s cubic-bezier(.2,.7,.2,1)";
  return (
    <div ref={ref} data-testid={testId} data-mode={mode} data-zoom={z.toFixed(2)} className="relative w-full overflow-hidden bg-[#A9B8A3]" style={{ aspectRatio: `${SCENE.w} / ${SCENE.h}` }}>
      <img src={SCENE.src} alt="Pared verde salvia sobre una consola de madera" draggable={false} className="absolute left-0 top-0 h-full w-full origin-top-left"
        style={{ transform: `translate(${tx}px, ${ty}px) scale(${z})`, transition: `transform ${ease}` }} />
      {W > 0 && (
        <div data-testid={`${testId}-plate`} data-wcm={wcm} data-hcm={hcm} onClick={onPlateClick} className={`absolute ${onPlateClick ? "cursor-pointer" : ""}`}
          style={{ left, top, width: pw, height: ph, transition: `left ${ease}, top ${ease}, width ${ease}, height ${ease}` }}>
          <div className="pointer-events-none absolute" style={{ inset: "14%", borderRadius: r, boxShadow: `${0.5 * k}px ${0.9 * k}px ${1.4 * k}px rgba(30,42,30,0.3)` }} />
          <div className="absolute inset-0 overflow-hidden" style={{ borderRadius: r, boxShadow: `${0.9 * k}px ${1.5 * k}px ${2.6 * k}px -${0.2 * k}px rgba(30,42,30,0.38), ${0.15 * k}px ${0.25 * k}px ${0.4 * k}px rgba(30,42,30,0.25)` }}>
            <PlateCells w={pw} h={ph} rects={rects} cells={cells} photos={photos} orientation={orientation} />
            <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(118deg,rgba(255,255,255,0.22)_0%,rgba(255,255,255,0)_38%,rgba(255,255,255,0)_70%,rgba(255,255,255,0.08)_100%)]" />
          </div>
          <div className="absolute left-0" style={{ top: -16 }}><Arrow length={pw} label={`${wcm} cm`} testId={`${testId}-dim-width`} /></div>
          <div className="absolute top-0" style={{ left: pw + 16 }}><Arrow vertical length={ph} label={`${hcm} cm`} testId={`${testId}-dim-height`} /></div>
        </div>
      )}
      {(mode === "detail" || empty) && <div className="absolute left-2 top-2 flex flex-wrap gap-1.5 sm:left-3 sm:top-3">{mode === "detail" && <Pill testId={`${testId}-zoom-badge`} className="sm:text-[0.7rem]">Vista ampliada</Pill>}{empty && <Pill testId={`${testId}-example-badge`}>Imagen de ejemplo</Pill>}</div>}
      {label && <span data-testid={`${testId}-label`} className="absolute bottom-3 right-3 hidden rounded-full bg-[#080808]/80 px-3 py-1 text-[0.7rem] font-medium tracking-[0.08em] text-[#FAF8F4] sm:inline-block">{label}</span>}
      <div className="absolute bottom-3 left-3 flex flex-col items-start gap-1.5">
        <span data-testid={`${testId}-approx`} className="rounded-full bg-[#FAF8F4]/90 px-3 py-1.5 text-[0.62rem] leading-tight text-[#080808]/80 sm:text-[0.7rem]">
          {mode === "detail" ? "Vista ampliada para apreciar tu diseño." : <><span className="sm:hidden">Visualización aproximada</span><span className="hidden sm:inline">Visualización aproximada a escala · referencia: consola de 180 cm.</span></>}
        </span>
      </div>
    </div>
  );
};
